import { IAppContext } from 'App/AppContext';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import { CopilotConversation } from 'models/copilot/copilotConversation';
import { CopilotPanelRuntime } from 'models/copilot/copilotPanelContextDefinition';
import { copilotPanelResultBus } from 'models/copilot/copilotPanelResultBus';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { TenantProcess } from 'models/copilot/tenantProcessStateBase';
import ResourceList from 'models/resourceList';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import {
  mergeAdaptiveInputs,
  openSharepointConsentConversation,
  setSharePointLibraryFromConsent,
  setSharePointReportFromConsent,
} from './copilotConversationHelpers';
import { getProcessActions, resolveRuntimeAction } from './copilotPanelHelpers';

interface IUseCopilotRuntimeProps {
  appContext: IAppContext;
  content: CopilotPanelContentBase | undefined;
  conversation: CopilotConversation | undefined;
  actions: CopilotAction[];
  activeAction: CopilotAction | undefined;
  copilotSessionId: number | undefined;
  copilotContextKey: string;
  previewContent: CopilotPreviewContent;
  setCopilotSessionId: Dispatch<SetStateAction<number | undefined>>;
  setPreviewContent: Dispatch<SetStateAction<CopilotPreviewContent>>;
  setConversation: Dispatch<SetStateAction<CopilotConversation | undefined>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setStreamStatus: Dispatch<SetStateAction<string | undefined>>;
  createSelectedDocumentRequest: () => CopilotPanelRuntime['selectedDocument'];
  getInstructionExternalId?: () => string | undefined;
  getUsageState: <TState>(context: CopilotPanelContext) => TState | undefined;
  onStartProcess: (action: CopilotAction) => Promise<void>;
  onEndProcess: (processId: TenantProcess) => Promise<void>;
  submitConversation: (
    conversation: CopilotConversation,
    actions: CopilotAction[],
    targetFolder?: ResourceList,
  ) => Promise<void>;
}

export const useCopilotRuntime = ({
  appContext,
  content,
  conversation,
  actions,
  activeAction,
  copilotSessionId,
  copilotContextKey,
  previewContent,
  setCopilotSessionId,
  setPreviewContent,
  setConversation,
  setIsLoading,
  setStreamStatus,
  createSelectedDocumentRequest,
  getInstructionExternalId,
  getUsageState,
  onStartProcess,
  onEndProcess,
  submitConversation,
}: IUseCopilotRuntimeProps) => {
  // The one process this usage offers: what the consent card starts once the integrations are complete,
  // and what a refused start is retried with. Resolved when INVOKED, not when the runtime was built: the
  // card can be opened right after a scene change, and the panel's actions for the new context arrive a
  // render later - a closure over that render's actions would start the previous scene's process.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const activeActionRef = useRef(activeAction);
  activeActionRef.current = activeAction;
  const onStartProcessRef = useRef(onStartProcess);
  onStartProcessRef.current = onStartProcess;

  const startActiveProcess = useCallback((): Promise<void> => {
    // the consent card and a refused start restart the first process, never the help prompt the list starts with
    const processAction = activeActionRef.current ?? getProcessActions(actionsRef.current)[0] ?? actionsRef.current[0];
    if (!processAction) return Promise.resolve();

    return onStartProcessRef.current(processAction);
  }, []);

  const getRuntime = useCallback(
    (
      runtimeConversation: CopilotConversation = conversation ?? new CopilotConversation(),
      runtimeActions: CopilotAction[] = actions,
      connectionId: string | null = null,
      accessToken: string = '',
      targetFolder?: ResourceList,
      abortSignal?: AbortSignal,
    ): CopilotPanelRuntime => ({
      appContext,
      content,
      conversation: runtimeConversation,
      actions: runtimeActions,
      action: resolveRuntimeAction(runtimeActions, activeAction),
      connectionId,
      accessToken,
      copilotSessionId,
      copilotContextKey,
      selectedDocument: createSelectedDocumentRequest(),
      instructionExternalId: getInstructionExternalId?.(),
      setCopilotSessionId,
      targetFolder,
      resultBus: copilotPanelResultBus,
      previewContent,
      setPreviewContent,
      setConversation,
      setIsLoading,
      setNewStatus: setStreamStatus,
      mergeAdaptiveInputs,
      abortSignal,
      setSharePointLibraryFromConsent: (checked: boolean, getPermissions: boolean) =>
        setSharePointLibraryFromConsent(appContext, checked, getPermissions),
      setSharePointReportFromConsent: (checked: boolean, getPermissions: boolean) =>
        setSharePointReportFromConsent(appContext, checked, getPermissions),
      openSharepointConsentConversation: () =>
        openSharepointConsentConversation(setConversation, setIsLoading, startActiveProcess),
      submitConversation: async (nextConversation, nextTargetFolder) =>
        submitConversation(nextConversation, runtimeActions, nextTargetFolder),
      getUsageState,
      endProcess: onEndProcess,
      startActiveProcess,
    }),
    [
      actions,
      activeAction,
      appContext,
      content,
      conversation,
      copilotContextKey,
      copilotSessionId,
      createSelectedDocumentRequest,
      getInstructionExternalId,
      getUsageState,
      onEndProcess,
      previewContent,
      setConversation,
      setCopilotSessionId,
      setIsLoading,
      setPreviewContent,
      setStreamStatus,
      startActiveProcess,
      submitConversation,
    ],
  );

  return getRuntime;
};
